// /compare/pixieset: Pixally vs Pixieset.
//
// Read the header of honeybook.js first: every rule there applies here. Pixieset's
// facts, with sources and exact words: docs/geo/compare-gallery-platforms-evidence.md.
// Prints: Pixally sells prints and albums from the gallery, shipped to the US and
// Canada only (Ryan, 2 Oct 2026: on from launch; the /preview gallery section
// says the same). Which plans include it is not settled, so no page names a plan
// for it. The competitor's store stays on the page as its own edge.

export const ROUTE = '/compare/pixieset';
export const UPDATED_AT = '2026-10-05';
export const CHECKED_AT = 'October 2, 2026';
export const CHECKED_LABEL = 'Pixieset facts checked';
export const HERO_ART = 'versus';

export const TITLE = 'Pixally vs Pixieset';
export const SEO_TITLE = 'Pixally vs Pixieset for Wedding Photographers and Videographers';
export const SEO_DESCRIPTION =
  'How Pixally and Pixieset compare on price, galleries, video, print sales and CRM tools for wedding photographers and videographers, with every Pixieset fact sourced.';

export const LEDE =
  'Pixieset is a client gallery platform with a print store, plus a separate CRM (Studio Manager) and website builder, each with a free plan and all three bundled in its Suite. Pixally is a CRM built for wedding photographers and videographers, launching on January 1, 2027, with photo, film and hybrid galleries and print sales built in.';

export const BODY = [
  {
    type: 'paragraph',
    text: 'If you sell prints and products, or want a free gallery today, Pixieset is a strong choice. If you deliver a lot of film or run a crew, the differences below matter.',
  },
  { type: 'heading', level: 2, text: 'Side by side' },
  {
    type: 'paragraph',
    text: `The Pixieset column comes from Pixieset's own pricing pages, checked ${CHECKED_AT}.`,
  },
  {
    type: 'table',
    label: 'Pixally and Pixieset compared',
    head: ['', 'Pixally', 'Pixieset'],
    rows: [
      ['Available', 'From January 1, 2027', 'Now'],
      ['Free plan or trial', 'A 14-day free trial with no card, then the Free plan: up to 5 active booked projects (no gallery storage)', 'Yes: Client Gallery with 3 GB, and Studio Manager'],
      ['Galleries and CRM together, billed yearly', 'Basic $33, Pro $58, Studio $99 a month', 'Suite: Starter $28, Pro $38, Ultimate $55 a month'],
      ['Photo storage', '200 GB, 1 TB, unlimited', 'Suite: 100 GB, 2,000 GB, unlimited'],
      ['Video', '50 GB to start on paid plans, then packs from $50 for 50 GB; no limit on film length', 'Suite: 1, 4 or 10 hours; $5 a month per extra hour'],
      ['Print and product store', 'Yes: prints and albums, shipped to the US and Canada', 'Yes, commission-free on paid plans'],
      ['Website builder', 'No', 'Yes, in the Suite'],
      ['Second shooters and contractors', 'Free contractor accounts; second shooter management and a contractor portal on Pro and Studio', 'Not described on Pixieset\'s pricing pages'],
      ['Post-production tracking', 'On Pro and Studio', 'Not described on Pixieset\'s pricing pages'],
    ],
  },
  { type: 'heading', level: 2, text: 'When Pixieset is the better choice' },
  {
    type: 'list',
    items: [
      'It exists today. Pixally launches on January 1, 2027.',
      'Print and product sales are a large part of your business: Pixieset\'s store is commission-free on paid plans. Pixally sells prints and albums too, shipped only to the US and Canada.',
      'It is cheaper to get galleries and a CRM together: the Suite starts at $28 a month billed yearly and includes a website.',
      'More photo storage at the middle tier: the Suite\'s Pro plan includes 2,000 GB, against 1 TB on Pixally Pro.',
      'Free plans for both its galleries and its CRM.',
    ],
  },
  { type: 'heading', level: 2, text: 'When Pixally is the better choice' },
  {
    type: 'paragraph',
    text: 'Pixally is the better choice if you deliver a lot of film, run second shooters and editors, or want your bookings, contracts, payments, edit and delivery in one system.',
  },
  { type: 'heading', level: 3, text: 'Film delivery without a ceiling' },
  { type: 'art', name: 'film' },
  {
    type: 'paragraph',
    text: 'Pixieset\'s video is counted in hours by plan: 1, 4 or 10 in the Suite, and $5 a month for each extra hour. Pixally puts no limit on film length, plays films with chapters so a couple can jump to the ceremony or the first dance, and sells delivery in packs (50 GB for $50, 100 GB for $95, 500 GB for $450) that never expire.',
  },
  { type: 'heading', level: 3, text: 'The CRM and the delivery in one place' },
  { type: 'art', name: 'pipeline' },
  {
    type: 'paragraph',
    text: 'With Pixieset the galleries and Studio Manager are separate apps bundled together. In Pixally the same project carries the inquiry, proposal, contract, payments, the edit through post-production (Pro and Studio), and the delivered gallery.',
  },
  { type: 'heading', level: 3, text: 'Second shooters and contractors' },
  { type: 'art', name: 'crew' },
  {
    type: 'paragraph',
    text: "Second shooters get a [free contractor account](/contractor-terms) that works across every studio they shoot for, with their availability, travel fee and tax form in one place. Second shooter management and the contractor portal are on Pro (up to 10) and Studio (unlimited).",
  },
  { type: 'heading', level: 2, text: 'Switching to Pixally' },
  { type: 'art', name: 'switching' },
  {
    type: 'paragraph',
    text: 'Pixally will move your last 12 months of active client projects across for free, and turn your existing documents into Pixally templates. Winter, when the season is quiet, is the easiest time to switch.',
  },
];

export const FAQ = [
  {
    q: 'Is Pixieset a CRM?',
    a: 'Partly. Pixieset Studio Manager handles invoices, contracts, questionnaires and booking, free or as part of the Pixieset Suite with its galleries and website.',
  },
  {
    q: 'How much video does Pixieset include?',
    a: 'In the Suite, 1, 4 or 10 hours by plan; Client Gallery plans include 30 minutes to 5 hours. Extra video is $5 a month per hour.',
  },
  {
    q: 'Can I sell prints with Pixally?',
    a: 'Yes. Couples can order prints and albums from their gallery, shipped to the United States and Canada.',
  },
  {
    q: 'Is Pixally available yet?',
    a: 'No. Pixally launches on January 1, 2027. You can join the waitlist at pixally.com.',
  },
];

export const SOURCES = [
  { label: 'Pixieset Client Gallery pricing', url: 'https://pixieset.com/pricing/' },
  { label: 'Pixieset Suite pricing', url: 'https://pixieset.com/pricing-suite/' },
  { label: 'Pixieset Studio Manager pricing', url: 'https://pixieset.com/pricing-studio-manager/' },
  { label: 'Pixally plans and video packs: the Pixally pricing table', url: null },
  { label: 'Pixally contractor accounts: Contractor Terms of Use, Section 5.1', url: '/contractor-terms' },
];
